import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DomainModel, DomainValidityStatusesEnum} from '../../../models/api/domainModel';

/*
 * Types.
 */

interface EmailDomainTokenProps {
  domain: DomainModel;
  isSelected: boolean;
}

interface DomainTokenStyleProps {
  $isSelected: boolean;
  $isInvalid: boolean;
}

/*
 * Style.
 */

const StyledDomainTokenDiv = styled.div<DomainTokenStyleProps>`
  display: flex;
  align-items: center;
  gap: 4px;

  border-radius: 4px;
  padding: 3px 6px;
  margin: 1px 4px 1px 0;

  color: ${(p) => p.theme.greys.shade90};
  background-color: ${(p) => p.theme.alphas.black30};
  ${(p) => addColorStyles(p)};
`;

function addColorStyles(props: DomainTokenStyleProps) {
  if (props.$isInvalid && props.$isSelected) {
    return css`
      color: ${(p) => p.theme.greys.white};
      background-color: ${(p) => p.theme.palette.red.shade50};
    `;
  }

  if (props.$isInvalid) {
    return css`
      color: ${(p) => p.theme.greys.shade90};
      background-color: ${(p) => p.theme.palette.red.shade30};
    `;
  }

  if (props.$isSelected) {
    return css`
      color: ${(p) => p.theme.greys.white};
      background-color: ${(p) => p.theme.greys.shade70};
    `;
  }

  return css`
    &:hover {
      color: ${(p) => p.theme.greys.shade90};
      background-color: ${(p) => p.theme.alphas.black40};
    }
  `;
}

const StyledDomainNameDiv = styled.div`
  ${ellipsis()};
`;

/*
 * Component.
 */

export const EmailDomainToken: FC<EmailDomainTokenProps> = ({domain, isSelected}) => {
  const theme = useUserTheme();
  const isInvalid =
    domain.validityStatus === DomainValidityStatusesEnum.INVALID_CONFLICT ||
    domain.validityStatus === DomainValidityStatusesEnum.INVALID_COMMON;
  return (
    <StyledDomainTokenDiv $isSelected={isSelected} $isInvalid={isInvalid}>
      <StyledDomainNameDiv>{domain.name}</StyledDomainNameDiv>
      {isInvalid && (
        <Icon
          name="warningFilledSmall"
          size={12}
          color={isSelected ? theme.greys.white : theme.palette.red.shade50}
        />
      )}
    </StyledDomainTokenDiv>
  );
};
